import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import SendIcon from "@material-ui/icons/Send";
import StopIcon from "@material-ui/icons/Stop";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import useThemeColors from "../../hooks/useThemeColors";
import { useTranslation } from "react-i18next";
import { useAIChatFilters } from "../../contexts/AIChatFiltersContext";

const MAX_CHARS = 2000;

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "10px 16px",
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#fff",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterToggleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingLeft: "2px",
    minWidth: 0,
    overflow: "hidden",
  },
  toggleSwitch: {
    position: "relative",
    display: "inline-block",
    width: "34px",
    height: "18px",
    cursor: "pointer",
    flexShrink: 0,
  },
  toggleSwitchInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  toggleSwitchSlider: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#ccc",
    borderRadius: "20px",
    transition: "background-color 0.3s",
    "&::before": {
      content: '""',
      position: "absolute",
      height: "14px",
      width: "14px",
      left: "2px",
      bottom: "2px",
      backgroundColor: "#fff",
      borderRadius: "50%",
      transition: "transform 0.3s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    },
  },
  toggleSwitchSliderChecked: {
    "&::before": {
      transform: "translateX(16px)",
    },
  },
  toggleLabel: {
    fontSize: "12px",
    fontWeight: 400,
    color: grayColor[3],
    userSelect: "none",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  textareaWrapper: {
    flex: 1,
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  textarea: {
    width: "100%",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "7px 12px",
    margin: 0,
    display: "block",
    fontSize: "14px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: "1.5",
    color: "#555",
    resize: "none",
    outline: "none",
    maxHeight: "120px",
    overflowY: "hidden",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease",
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "2px",
    },
    "&:focus": {
      borderColor: "#999",
    },
    "&::placeholder": {
      color: "#aaa",
    },
  },
  textareaOverflow: {
    overflowY: "auto",
    scrollbarColor: grayColor[6] + " transparent",
    "&::-webkit-scrollbar-thumb": {
      background: grayColor[6],
      borderRadius: "2px",
    },
  },
  charCount: {
    position: "absolute",
    bottom: "4px",
    right: "8px",
    fontSize: "10px",
    color: "#bbb",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    pointerEvents: "none",
  },
  charCountWarning: {
    color: "#e57373",
  },
  sendButton: {
    color: "#fff",
    width: 36,
    height: 36,
    flexShrink: 0,
    "&:disabled": {
      opacity: 0.4,
    },
  },
  stopButton: {
    color: "#fff",
    backgroundColor: "#e57373",
    width: 36,
    height: 36,
    flexShrink: 0,
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },
}));

export default function ChatInput({ onSend, onStop, disabled, autoFocus }) {
  const classes = useStyles();
  const { clientColor, clientTextColor } = useThemeColors();
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [hasOverflow, setHasOverflow] = useState(false);
  const [includeFilters, setIncludeFilters] = useState(true);
  const textareaRef = useRef(null);
  const { pageName, filters } = useAIChatFilters();
  const hasFilters = filters && filters.length > 0;

  const filterSummary = useMemo(() => {
    if (!hasFilters) return "";
    const parts = filters.map((f) => {
      const vals =
        f.values.length === 1
          ? f.values[0]
          : `${f.values.length} ${t("ai_chat.items", { defaultValue: "items" })}`;
      return `${f.dimension}: ${vals}`;
    });
    return parts.join(" | ");
  }, [filters, hasFilters, t]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      const timer = setTimeout(() => textareaRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxH = 120;
      textareaRef.current.style.height = Math.min(scrollHeight, maxH) + "px";
      setHasOverflow(scrollHeight > maxH);
    }
  }, [text]);

  const handleChange = (e) => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS) {
      setText(val);
    }
  };

  const handleSend = () => {
    if (text.trim() && !disabled) {
      const filterContext =
        hasFilters && includeFilters ? { pageName, filters } : null;
      onSend(text.trim(), filterContext);
      setText("");
      setHasOverflow(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const showCharCount = text.length > MAX_CHARS * 0.8;

  return (
    <div className={classes.container}>
      <div className={classes.inputRow}>
        <div className={classes.textareaWrapper}>
          <textarea
            ref={textareaRef}
            className={`${classes.textarea} ${hasOverflow ? classes.textareaOverflow : ""}`}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={t("ai_chat.input_placeholder", {
              defaultValue: "Escribe tu pregunta...",
            })}
            disabled={disabled}
            rows={3}
            maxLength={MAX_CHARS}
          />
          {showCharCount && (
            <span
              className={`${classes.charCount} ${text.length > MAX_CHARS * 0.95 ? classes.charCountWarning : ""}`}
            >
              {text.length}/{MAX_CHARS}
            </span>
          )}
        </div>
        {disabled && onStop ? (
          <IconButton
            className={classes.stopButton}
            onClick={onStop}
            size="small"
          >
            <StopIcon style={{ fontSize: 18 }} />
          </IconButton>
        ) : (
          <IconButton
            className={classes.sendButton}
            style={{ backgroundColor: clientColor, color: clientTextColor }}
            onClick={handleSend}
            disabled={disabled || !text.trim()}
            size="small"
          >
            <SendIcon style={{ fontSize: 18 }} />
          </IconButton>
        )}
      </div>
      {hasFilters && (
        <div className={classes.filterToggleRow}>
          <label className={classes.toggleSwitch}>
            <input
              type="checkbox"
              className={classes.toggleSwitchInput}
              checked={includeFilters}
              onChange={(e) => setIncludeFilters(e.target.checked)}
            />
            <span
              className={`${classes.toggleSwitchSlider} ${
                includeFilters ? classes.toggleSwitchSliderChecked : ""
              }`}
              style={
                includeFilters ? { backgroundColor: clientColor } : undefined
              }
            />
          </label>
          <span className={classes.toggleLabel} title={filterSummary}>
            {t("ai_chat.include_filters", {
              defaultValue: "Include current filters as context",
            })}
            {filterSummary ? ` — ${filterSummary}` : ""}
          </span>
        </div>
      )}
    </div>
  );
}

ChatInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  onStop: PropTypes.func,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
};
